import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, ShieldCheck, Sprout, ArrowRight, Truck, Wallet, HelpCircle, BadgeCheck } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Seo } from "@/components/Seo";

const HERO = "https://images.pexels.com/photos/15507929/pexels-photo-15507929.jpeg?auto=compress&cs=tinysrgb&w=1800";
const ABOUT_IMG = "https://static.prod-images.emergentagent.com/jobs/844ec571-477c-403b-8a80-3c98d9574361/images/8d60279fa9eafc7464cf11f17439ccf8169e31854499570a1a5410d6eb3ed434.jpeg";

const HOME_FAQS = [
  { q: "How do I place an order and pay?", a: "Add products to your cart and check out. We use Cash on Delivery — you pay in GHS only when your order arrives. No online payment needed." },
  { q: "Where do you deliver?", a: "Nationwide, across all 16 regions of Ghana — straight to your farm or home. Track every step from your account dashboard." },
  { q: "Are your products certified organic?", a: "Yes — our range carries approvals and listings such as CRIG, EPA Ghana, EU Organic, ECOCERT and OMRI. Each product page shows its certifications." },
  { q: "Can I get farming advice for my crops?", a: "Absolutely. Message us on WhatsApp or call +233 24 648 8119 and we'll recommend the right products and quantities for your farm." },
];

const CERTS = ["ECOCERT", "CRIG", "EPA Ghana", "EU Organic", "OMRI"];

const VALUES = [
  { icon: Leaf, title: "Eco Friendly", desc: "Inputs that work with nature, protecting soil and water." },
  { icon: ShieldCheck, title: "Certified Organic", desc: "Trusted, certified organic solutions for serious farmers." },
  { icon: Sprout, title: "Non Toxic", desc: "Safe for your crops, your family and beneficial insects." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <Seo
        title="Organic Farming Solutions, Ghana"
        path="/"
        description="Yayra Glover Organic Inputs — premium certified-organic fertilisers (PhytoGreen), organic insecticide (AgroPy 5EW), neem oil and soil care for Ghanaian farms. Order online, pay cash on delivery, nationwide."
      />
      {/* Hero */}
      <section className="relative noise-overlay min-h-[92vh] flex items-center bg-brand overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Ripe cacao pods on the tree" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand via-brand/90 to-brand/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand/85 via-transparent to-brand/40" />
        </div>
        <div className="absolute -right-24 top-1/3 w-96 h-96 rounded-full bg-brand-ochre/20 blur-3xl pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-6 py-24 w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.28em] text-brand-ochre font-semibold">Certified Organic · Ghana</span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl text-brand-bg leading-[1.05] tracking-tight">
              Grow more, naturally. Premium organic inputs for Ghanaian farms.
            </h1>
            <p className="mt-6 text-brand-bg/85 text-base sm:text-lg max-w-xl leading-relaxed">
              From Phytogreen foliar fertilisers to Agropy organic pest control — nourish your harvests
              without harmful chemicals. Order online, pay cash on delivery.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/shop"><Button data-testid="hero-shop-btn" className="bg-brand-terracotta hover:bg-brand-terracotta/90 text-white rounded-full h-13 px-8 text-base gap-2">Shop Products <ArrowRight className="w-4 h-4" /></Button></Link>
              <Link to="/about"><Button data-testid="hero-about-btn" variant="outline" className="rounded-full h-13 px-8 text-base bg-transparent border-brand-bg/40 text-brand-bg hover:bg-brand-bg hover:text-brand">Our Story</Button></Link>
            </div>
            <div className="mt-10" data-testid="hero-trust-badges">
              <p className="text-[11px] uppercase tracking-[0.22em] text-brand-bg/60 font-semibold flex items-center gap-2">
                <BadgeCheck className="w-4 h-4 text-brand-ochre" /> Certified Organic
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
                {CERTS.map((c) => (
                  <span key={c} className="text-brand-bg/85 font-display text-lg sm:text-xl tracking-wide">{c}</span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="mx-auto max-w-7xl px-6 -mt-16 relative z-10">
        <div className="grid gap-4 sm:grid-cols-3">
          {VALUES.map((v, i) => (
            <motion.div key={v.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-white rounded-sm border border-brand/10 shadow-sm p-6">
              <v.icon className="w-8 h-8 text-brand-terracotta" strokeWidth={1.5} />
              <h3 className="mt-4 font-display text-xl text-brand">{v.title}</h3>
              <p className="mt-1.5 text-sm text-brand-muted leading-relaxed">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-brand-muted font-semibold">Bestsellers</span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl text-brand">Farmer Favourites</h2>
          </div>
          <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-brand font-medium hover:gap-2.5 transition-all" data-testid="view-all-link">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {featured.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
        </div>
      </section>

      {/* About strip */}
      <section className="bg-brand-surface">
        <div className="mx-auto max-w-7xl px-6 py-20 grid gap-12 md:grid-cols-2 items-center">
          <div className="rounded-sm overflow-hidden">
            <img src={ABOUT_IMG} alt="Field officer inspecting a lush organic okra farm in Ghana" className="w-full h-[420px] object-cover" />
          </div>
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-brand-muted font-semibold">Rooted in Ghana</span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl text-brand leading-tight">Inspired by the father of certified organic cocoa in Ghana.</h2>
            <p className="mt-5 text-brand-muted leading-relaxed">
              Yayra Glover Organic Inputs brings the legacy of Yayra Glover's organic farming movement to every farmer.
              We formulate and supply organic fertilisers and crop protection that raise yields while keeping
              your land healthy for generations to come.
            </p>
            <Link to="/about"><Button data-testid="about-more-btn" className="mt-7 bg-brand hover:bg-brand-hover text-white rounded-full px-7">Learn More</Button></Link>
          </div>
        </div>
      </section>

      {/* FAQ teaser */}
      <section className="mx-auto max-w-3xl px-6 pt-20">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-brand-muted font-semibold">
            <HelpCircle className="w-4 h-4" /> Questions? We've got answers
          </span>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl text-brand">Frequently Asked</h2>
        </div>
        <Accordion type="single" collapsible className="w-full" data-testid="home-faq">
          {HOME_FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`hf-${i}`} data-testid={`home-faq-item-${i}`} className="border-brand/10">
              <AccordionTrigger data-testid={`home-faq-trigger-${i}`} className="text-left font-display text-lg text-brand hover:no-underline hover:text-brand-hover">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-brand-muted leading-relaxed text-sm">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="text-center mt-8">
          <Link to="/faq" data-testid="home-faq-all-link" className="inline-flex items-center gap-1.5 text-brand font-medium hover:gap-2.5 transition-all">
            View all FAQs <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* COD banner */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="flex items-start gap-4 bg-white border border-brand/10 rounded-sm p-7">
            <Wallet className="w-9 h-9 text-brand shrink-0" strokeWidth={1.5} />
            <div><h3 className="font-display text-xl text-brand">Cash on Delivery</h3><p className="text-sm text-brand-muted mt-1">Pay only when your order arrives. No online payment needed.</p></div>
          </div>
          <div className="flex items-start gap-4 bg-white border border-brand/10 rounded-sm p-7">
            <Truck className="w-9 h-9 text-brand shrink-0" strokeWidth={1.5} />
            <div><h3 className="font-display text-xl text-brand">Nationwide Delivery</h3><p className="text-sm text-brand-muted mt-1">We deliver across all 16 regions of Ghana, straight to your farm.</p></div>
          </div>
        </div>
      </section>
    </div>
  );
}
